import { useMemo, useState } from 'react';
import { AutocompleteSelect } from '@/components/ui/autocomplete-select';
import { cn } from '@/lib/utils';
import { useBeneficiarios } from '@/contabilidad/hooks/useBeneficiarios';

interface BeneficiarioOption {
  id: string;
  nombre: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
}

export function BeneficiarioAutocomplete({ value, onChange, placeholder, error }: Props) {
  const { beneficiarios, isLoading, createBeneficiario, isCreating } = useBeneficiarios();
  const [inputValue, setInputValue] = useState('');

  const normalizedSearch = inputValue.trim().toLowerCase();

  const options = useMemo<BeneficiarioOption[]>(() => {
    const filtered = normalizedSearch
      ? beneficiarios.filter((b) => b.nombre.toLowerCase().includes(normalizedSearch))
      : beneficiarios;
    return filtered.map((b) => ({ id: b.nombre, nombre: b.nombre }));
  }, [beneficiarios, normalizedSearch]);

  const canCreate = normalizedSearch.length > 0
    && !beneficiarios.some((b) => b.nombre.toLowerCase() === normalizedSearch);

  const selectedOption: BeneficiarioOption | null = value ? { id: value, nombre: value } : null;

  async function handleCreate(nombre: string) {
    const nuevo = await createBeneficiario({ nombre });
    onChange(nuevo.nombre);
    setInputValue('');
  }

  return (
    <AutocompleteSelect<BeneficiarioOption>
      mode="single"
      value={selectedOption}
      onChange={(option) => onChange(option?.nombre ?? '')}
      onClear={() => onChange('')}
      options={options}
      getOptionLabel={(option) => option.nombre}
      placeholder={placeholder ?? 'Seleccionar...'}
      ariaLabel="Beneficiario / Pagador"
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      isLoading={isLoading}
      emptyMessage="No se encontraron resultados."
      createOptionLabel={canCreate ? `Crear "${inputValue.trim()}"` : undefined}
      onCreate={canCreate ? handleCreate : undefined}
      isCreating={isCreating}
      className={cn(error && '[&_button[role=combobox]]:border-rose-500')}
    />
  );
}
